import type { ServiceCenterFilter } from './models/service-center-filter';
import { haversineMiles } from './geo-distance';

export interface DistanceAnchor { lat: number; lng: number; source: 'me' | 'point'; }
export type DistanceUnit = 'mi' | 'km';
export interface DistanceFilter { near: DistanceAnchor; radius: number; unit: DistanceUnit; }
export const METERS_PER_MILE = 1609.344;

// Allows a search across the continental US without globe-scale map circles.
const MAX_DISTANCE_MILES = 4000;
export function maxDistanceRadius(unit: DistanceUnit): number {
  return Math.floor(MAX_DISTANCE_MILES * (unit === 'km' ? METERS_PER_MILE / 1000 : 1));
}

export function validCoordinates(lat: unknown, lng: unknown): boolean {
  return typeof lat === 'number' && Number.isFinite(lat) && Math.abs(lat) <= 90
    && typeof lng === 'number' && Number.isFinite(lng) && Math.abs(lng) <= 180;
}

export function activeDistance(filter: Pick<ServiceCenterFilter, 'near' | 'radius' | 'unit'>): DistanceFilter | null {
  const { near, radius, unit } = filter;
  if (!near || !validCoordinates(near.lat, near.lng) || (near.source !== 'me' && near.source !== 'point')
    || typeof radius !== 'number' || !Number.isFinite(radius) || radius <= 0
    || (unit !== undefined && unit !== 'mi' && unit !== 'km')) return null;
  // Accept older kilometre links, but publish and render distance in miles.
  const miles = unit === 'km' ? radius / (METERS_PER_MILE / 1000) : radius;
  return { near, radius: Math.min(miles, maxDistanceRadius('mi')), unit: 'mi' };
}

export function radiusMeters(distance: DistanceFilter): number {
  return distance.radius * (distance.unit === 'km' ? 1000 : METERS_PER_MILE);
}

export function withinDistance(lat: number | null | undefined, lng: number | null | undefined, distance: DistanceFilter): boolean {
  if (!validCoordinates(lat, lng) || (lat === 0 && lng === 0)) return false;
  return haversineMiles(distance.near.lat, distance.near.lng, lat!, lng!) <= radiusMeters(distance) / METERS_PER_MILE;
}

export function distanceFromUrl(near: string | null, radius: string | null, unit: string | null, source: string | null = null): Partial<DistanceFilter> {
  const parts = near?.split(',');
  const cleared = { near: undefined, radius: undefined, unit: undefined };
  if (parts?.length !== 2 || parts.some(p => !p.trim()) || !radius?.trim()) return cleared;
  const candidate = {
    near: { lat: Number(parts[0]), lng: Number(parts[1]), source: source === 'me' ? 'me' as const : 'point' as const },
    radius: Number(radius), unit: (unit ?? 'mi') as DistanceUnit,
  };
  return activeDistance(candidate) ?? cleared;
}

export function distanceToUrl(filter: Pick<ServiceCenterFilter, 'near' | 'radius' | 'unit'>): Record<string, string> {
  const d = activeDistance(filter);
  return {
    near: d ? d.near.lat + ',' + d.near.lng : '',
    radius: d ? String(d.radius) : '',
    unit: d?.unit ?? '',
    nearSource: d?.near.source === 'me' ? 'me' : '',
  };
}

export function distanceKey(filter: Pick<ServiceCenterFilter, 'near' | 'radius' | 'unit'>): string {
  const d = activeDistance(filter);
  return d ? [d.near.lat, d.near.lng, d.radius, d.unit, d.near.source].join('|') : '';
}
